import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  User,
  Briefcase,
  Users,
  ImageIcon,
  Upload,
  X,
} from "lucide-react";
import { showToastNotification } from "@/utility/helper";
import Layout from "@/components/layout/Layout";
import MainHeading from "@/components/common/MainHeading";
import { Button } from "@/components/ui/button";
import { RoutesLink } from "@/utility/RouteLinks";

// ─── Types ──────────────────────────────────────────────────────────
interface IForm {
  employeeCode: string;
  fullName: string;
  gender: string;
  email: string;
  primaryPhone: string;
  secondaryPhone: string;
  site: string;
  department: string;
  shift: string;
  dailyRate: string;
  joiningDate: string;
  status: "active" | "inactive";
  emergencyContactPerson: string;
  emergencyContactPhone: string;
  profileImage: File | null;
  aadharPhoto: File | null;
  document: File | null;
}

// ─── Mock options (replace with API) ─────────────────────────────
const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

const DEPARTMENT_OPTIONS = [
  { value: "civil", label: "Civil" },
  { value: "electrical", label: "Electrical" },
  { value: "plumbing", label: "Plumbing" },
];

const SITE_OPTIONS = [
  { value: "site1", label: "Site 1 — Foundation" },
  { value: "site2", label: "Site 2 — Block A" },
  { value: "site3", label: "Site 3 — Interior" },
  { value: "site4", label: "Site 4 — Roofing" },
];

const SHIFT_OPTIONS = [
  { value: "day", label: "Day Shift (08:00 – 17:00)" },
  { value: "night", label: "Night Shift (20:00 – 05:00)" },
  { value: "half", label: "Half Day (08:00 – 13:00)" },
];

// ─── Styling constants ────────────────────────────────────────────
const inputClass =
  "w-full bg-white border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none transition-all";
const labelClass = "text-[13px] font-medium text-gray-700 mb-1.5 block";

// ─── Section wrapper ──────────────────────────────────────────────
const FormSection = ({
  icon,
  title,
  description,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  children: React.ReactNode;
}) => (
  <section className="border-b border-gray-100 last:border-b-0 px-6 py-7 sm:px-8">
    <div className="flex items-start gap-3 mb-6">
      <div className="w-9 h-9 rounded-lg bg-primary/8 text-primary flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div>
        <h3 className="text-[15px] font-semibold text-gray-900">{title}</h3>
        {description && (
          <p className="text-[13px] text-gray-400 mt-0.5">{description}</p>
        )}
      </div>
    </div>
    {children}
  </section>
);

// ─── Main Component ───────────────────────────────────────────────
const CreateSupervisor: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const [form, setForm] = useState<IForm>({
    employeeCode: "",
    fullName: "",
    gender: "",
    email: "",
    primaryPhone: "",
    secondaryPhone: "",
    site: "",
    department: "",
    shift: "",
    dailyRate: "",
    joiningDate: "",
    status: "active",
    emergencyContactPerson: "",
    emergencyContactPhone: "",
    profileImage: null,
    aadharPhoto: null,
    document: null,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof IForm, string>>>(
    {},
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // File refs
  const profileInputRef = useRef<HTMLInputElement>(null);
  const aadharInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);

  // ─── Fetch data for edit mode ──────────────────────────────────
  useEffect(() => {
    if (isEditMode) {
      const fetchSupervisor = async () => {
        setIsLoading(true);
        try {
          // TODO: Replace with API call
          const mockData: IForm = {
            employeeCode: "EMP-001",
            fullName: "Rahul Singh",
            gender: "male",
            email: "rahul@example.com",
            primaryPhone: "9876543210",
            secondaryPhone: "9876543211",
            site: "site1",
            department: "civil",
            shift: "day",
            dailyRate: "750",
            joiningDate: "2026-01-15",
            status: "active",
            emergencyContactPerson: "Sunita Devi",
            emergencyContactPhone: "9876543212",
            profileImage: null,
            aadharPhoto: null,
            document: null,
          };
          setForm(mockData);
        } catch (error) {
          showToastNotification("danger", "Failed to load supervisor data");
          navigate(RoutesLink.supervisors);
        } finally {
          setIsLoading(false);
        }
      };
      fetchSupervisor();
    }
  }, [id, isEditMode, navigate]);

  // ─── Handlers ──────────────────────────────────────────────────
  const handleChange = (field: keyof IForm, value: string | File | null) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleFileChange = (field: keyof IForm, file: File | null) => {
    if (file && file.size > 5 * 1024 * 1024) {
      showToastNotification("danger", "File size must be under 5MB");
      return;
    }
    setForm((prev) => ({ ...prev, [field]: file }));
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof IForm, string>> = {};
    if (!form.employeeCode.trim())
      newErrors.employeeCode = "Employee code is required";
    if (!form.fullName.trim()) newErrors.fullName = "Full name is required";
    if (!form.gender) newErrors.gender = "Please select gender";
    if (!form.email.trim()) newErrors.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(form.email))
      newErrors.email = "Invalid email format";
    if (!form.primaryPhone.trim())
      newErrors.primaryPhone = "Primary phone is required";
    else if (!/^\d{10}$/.test(form.primaryPhone))
      newErrors.primaryPhone = "Must be 10 digits";
    if (form.secondaryPhone && !/^\d{10}$/.test(form.secondaryPhone)) {
      newErrors.secondaryPhone = "Must be 10 digits";
    }
    if (!form.site) newErrors.site = "Please select a site";
    if (!form.department) newErrors.department = "Please select a department";
    if (!form.shift) newErrors.shift = "Please select a shift";
    if (!form.dailyRate) newErrors.dailyRate = "Daily rate is required";
    else if (isNaN(Number(form.dailyRate)) || Number(form.dailyRate) <= 0) {
      newErrors.dailyRate = "Enter a valid positive number";
    }
    if (!form.joiningDate) newErrors.joiningDate = "Joining date is required";
    if (!form.emergencyContactPerson.trim()) {
      newErrors.emergencyContactPerson = "Emergency contact person is required";
    }
    if (!form.emergencyContactPhone.trim()) {
      newErrors.emergencyContactPhone = "Emergency contact number is required";
    } else if (!/^\d{10}$/.test(form.emergencyContactPhone)) {
      newErrors.emergencyContactPhone = "Must be 10 digits";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      // TODO: API call with FormData for files
      await new Promise((resolve) => setTimeout(resolve, 1000));
      showToastNotification(
        "success",
        isEditMode ? "Supervisor updated" : "Supervisor created",
      );
      navigate(RoutesLink.supervisors);
    } catch (error) {
      showToastNotification("danger", "Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Render file upload ─────────────────────────────────────────
  const renderFileUpload = (
    label: string,
    file: File | null,
    onFileChange: (file: File | null) => void,
    inputRef:
      | React.RefObject<HTMLInputElement>
      | React.MutableRefObject<HTMLInputElement | null>,
    accept: string = "image/*",
  ) => {
    const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files?.[0];
      if (selected) onFileChange(selected);
    };
    const handleRemove = () => {
      onFileChange(null);
      if (inputRef.current) inputRef.current.value = "";
    };
    return (
      <div>
        <label className={labelClass}>{label}</label>
        {!file ? (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="relative w-full flex flex-col items-center justify-center gap-1.5 py-6 rounded-lg border-2 border-dashed border-gray-200 hover:border-primary/40 hover:bg-primary/[0.03] transition-colors cursor-pointer"
          >
            <Upload size={18} className="text-gray-400" />
            <span className="text-[13px] font-medium text-gray-500">
              Choose file
            </span>
            <input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              type="file"
              accept={accept}
              className="absolute inset-0 opacity-0 cursor-pointer"
              onChange={handleSelect}
            />
          </button>
        ) : (
          <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50/60 px-3 py-2.5">
            {file.type.startsWith("image/") ? (
              <img
                src={URL.createObjectURL(file)}
                alt="preview"
                className="h-10 w-10 object-cover rounded-md border border-gray-200 shrink-0"
              />
            ) : (
              <div className="h-10 w-10 rounded-md bg-white border border-gray-200 flex items-center justify-center shrink-0">
                <ImageIcon size={16} className="text-gray-400" />
              </div>
            )}
            <span className="text-[13px] text-gray-600 truncate flex-1 min-w-0">
              {file.name}
            </span>
            <button
              type="button"
              onClick={handleRemove}
              className="h-6 w-6 shrink-0 rounded-md flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
            >
              <X size={14} />
            </button>
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // ─── Render ──────────────────────────────────────────────────────
  return (
    <div className="max-w-5xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          className="h-9 w-9 p-0 hover:bg-muted shrink-0"
          onClick={() => navigate(RoutesLink.supervisors)}
          type="button"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <MainHeading
            title={isEditMode ? "Edit Supervisor" : "Add Supervisor"}
          />
          <p className="text-[13px] text-gray-400 -mt-0.5">
            {isEditMode
              ? "Update supervisor details"
              : "Register a new supervisor and assign site"}
          </p>
        </div>
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden"
      >
        {/* Basic Information */}
        <FormSection
          icon={<User size={17} />}
          title="Basic information"
          description="Identity and contact details"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-5">
            <div>
              <label className={labelClass}>
                Employee code <span className="text-red-500">*</span>
              </label>
              <input
                className={inputClass}
                placeholder="Enter employee code"
                value={form.employeeCode}
                onChange={(e) => handleChange("employeeCode", e.target.value)}
              />
              {errors.employeeCode && (
                <p className="text-xs text-red-500 mt-1.5">
                  {errors.employeeCode}
                </p>
              )}
            </div>
            <div>
              <label className={labelClass}>
                Full name <span className="text-red-500">*</span>
              </label>
              <input
                className={inputClass}
                placeholder="Enter full name"
                value={form.fullName}
                onChange={(e) => handleChange("fullName", e.target.value)}
              />
              {errors.fullName && (
                <p className="text-xs text-red-500 mt-1.5">{errors.fullName}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>
                Gender <span className="text-red-500">*</span>
              </label>
              <select
                className={inputClass}
                value={form.gender}
                onChange={(e) => handleChange("gender", e.target.value)}
              >
                <option value="">Select gender</option>
                {GENDER_OPTIONS.map((g) => (
                  <option key={g.value} value={g.value}>
                    {g.label}
                  </option>
                ))}
              </select>
              {errors.gender && (
                <p className="text-xs text-red-500 mt-1.5">{errors.gender}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>
                Email <span className="text-red-500">*</span>
              </label>
              <input
                className={inputClass}
                type="email"
                placeholder="john@example.com"
                value={form.email}
                onChange={(e) => handleChange("email", e.target.value)}
              />
              {errors.email && (
                <p className="text-xs text-red-500 mt-1.5">{errors.email}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>
                Primary mobile no. <span className="text-red-500">*</span>
              </label>
              <input
                className={inputClass}
                placeholder="10-digit mobile number"
                value={form.primaryPhone}
                onChange={(e) => handleChange("primaryPhone", e.target.value)}
              />
              {errors.primaryPhone && (
                <p className="text-xs text-red-500 mt-1.5">
                  {errors.primaryPhone}
                </p>
              )}
            </div>
            <div>
              <label className={labelClass}>Secondary mobile no.</label>
              <input
                className={inputClass}
                placeholder="10-digit mobile number"
                value={form.secondaryPhone}
                onChange={(e) => handleChange("secondaryPhone", e.target.value)}
              />
              {errors.secondaryPhone && (
                <p className="text-xs text-red-500 mt-1.5">
                  {errors.secondaryPhone}
                </p>
              )}
            </div>
          </div>
        </FormSection>

        {/* Documents */}
        <FormSection
          icon={<ImageIcon size={17} />}
          title="Documents"
          description="JPG or PDF, up to 5MB each"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-5">
            {renderFileUpload(
              "Profile image",
              form.profileImage,
              (file) => handleFileChange("profileImage", file),
              profileInputRef,
              "image/*",
            )}
            {renderFileUpload(
              "Aadhar photo",
              form.aadharPhoto,
              (file) => handleFileChange("aadharPhoto", file),
              aadharInputRef,
              "image/*",
            )}
            {renderFileUpload(
              "Document",
              form.document,
              (file) => handleFileChange("document", file),
              documentInputRef,
              ".pdf,.doc,.docx,image/*",
            )}
          </div>
        </FormSection>

        {/* Work Details */}
        <FormSection
          icon={<Briefcase size={17} />}
          title="Work details"
          description="Site, shift, and compensation"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-5">
            <div>
              <label className={labelClass}>
                Site <span className="text-red-500">*</span>
              </label>
              <select
                className={inputClass}
                value={form.site}
                onChange={(e) => handleChange("site", e.target.value)}
              >
                <option value="">Select site</option>
                {SITE_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              {errors.site && (
                <p className="text-xs text-red-500 mt-1.5">{errors.site}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>
                Department <span className="text-red-500">*</span>
              </label>
              <select
                className={inputClass}
                value={form.department}
                onChange={(e) => handleChange("department", e.target.value)}
              >
                <option value="">Select department</option>
                {DEPARTMENT_OPTIONS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
              {errors.department && (
                <p className="text-xs text-red-500 mt-1.5">
                  {errors.department}
                </p>
              )}
            </div>
            <div>
              <label className={labelClass}>
                Shift <span className="text-red-500">*</span>
              </label>
              <select
                className={inputClass}
                value={form.shift}
                onChange={(e) => handleChange("shift", e.target.value)}
              >
                <option value="">Select shift</option>
                {SHIFT_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              {errors.shift && (
                <p className="text-xs text-red-500 mt-1.5">{errors.shift}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>
                Daily rate (₹) <span className="text-red-500">*</span>
              </label>
              <input
                className={inputClass}
                type="number"
                min={0}
                step="0.01"
                placeholder="e.g. 750"
                value={form.dailyRate}
                onChange={(e) => handleChange("dailyRate", e.target.value)}
              />
              {errors.dailyRate && (
                <p className="text-xs text-red-500 mt-1.5">
                  {errors.dailyRate}
                </p>
              )}
            </div>
            <div>
              <label className={labelClass}>
                Joining date <span className="text-red-500">*</span>
              </label>
              <input
                className={inputClass}
                type="date"
                value={form.joiningDate}
                onChange={(e) => handleChange("joiningDate", e.target.value)}
              />
              {errors.joiningDate && (
                <p className="text-xs text-red-500 mt-1.5">
                  {errors.joiningDate}
                </p>
              )}
            </div>
            <div>
              <label className={labelClass}>Status</label>
              <div className="flex gap-2">
                {(["active", "inactive"] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => handleChange("status", s)}
                    className={`flex-1 rounded-lg border px-3.5 py-2.5 text-sm font-medium capitalize transition-colors cursor-pointer ${
                      form.status === s
                        ? s === "active"
                          ? "border-teal-200 bg-teal-50 text-teal-700"
                          : "border-gray-300 bg-gray-100 text-gray-700"
                        : "border-gray-200 bg-white text-gray-400 hover:bg-gray-50"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </FormSection>

        {/* Emergency Contact */}
        <FormSection
          icon={<Users size={17} />}
          title="Emergency contact"
          description="Who to reach in case of an incident"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-5">
            <div>
              <label className={labelClass}>
                Contact person <span className="text-red-500">*</span>
              </label>
              <input
                className={inputClass}
                placeholder="Enter contact person"
                value={form.emergencyContactPerson}
                onChange={(e) =>
                  handleChange("emergencyContactPerson", e.target.value)
                }
              />
              {errors.emergencyContactPerson && (
                <p className="text-xs text-red-500 mt-1.5">
                  {errors.emergencyContactPerson}
                </p>
              )}
            </div>
            <div>
              <label className={labelClass}>
                Contact number <span className="text-red-500">*</span>
              </label>
              <input
                className={inputClass}
                placeholder="10-digit mobile number"
                value={form.emergencyContactPhone}
                onChange={(e) =>
                  handleChange("emergencyContactPhone", e.target.value)
                }
              />
              {errors.emergencyContactPhone && (
                <p className="text-xs text-red-500 mt-1.5">
                  {errors.emergencyContactPhone}
                </p>
              )}
            </div>
          </div>
        </FormSection>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 px-6 sm:px-8 py-4 bg-gray-50/70 border-t border-gray-100">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(RoutesLink.supervisors)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" className="gap-2" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {isSubmitting
              ? "Saving…"
              : isEditMode
                ? "Update Supervisor"
                : "Save Supervisor"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateSupervisor;
